import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import { GiBull } from "react-icons/gi";
import { ImParagraphRight } from "react-icons/im";
import { api } from "../../../services/api";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const OPCOES_PERIODO = [
  { label: "7 dias", valor: 7 },
  { label: "30 dias", valor: 30 },
  { label: "90 dias", valor: 90 },
  { label: "180 dias", valor: 180 },
  { label: "365 dias", valor: 365 },
];

const CotHist = () => {
  const [dias, setDias] = useState(30);
  const [historico, setHistorico] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    buscarHistorico();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dias]);

  const buscarHistorico = async () => {
    setCarregando(true);
    setErro(null);

    try {
      const response = await api.get("/mercado/boi-gordo/historico/", {
        params: { dias },
      });
      setHistorico(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Erro ao carregar histórico de cotações:", error);
      setErro("Não foi possível carregar o histórico de cotações.");
      setHistorico([]);
    } finally {
      setCarregando(false);
    }
  };

  const formatarData = (data) => {
    if (!data) return "-";
    return new Date(data).toLocaleDateString("pt-BR", { timeZone: "UTC" });
  };

  const formatarMoeda = (valor) => {
    if (valor === null || valor === undefined || Number.isNaN(valor)) return "-";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(valor);
  };

  const precos = historico.map((item) => Number(item.preco_arroba) || 0);
  const precoAtual = precos.length > 0 ? precos[precos.length - 1] : null;
  const precoMedio =
    precos.length > 0 ? precos.reduce((soma, p) => soma + p, 0) / precos.length : null;
  const precoMaximo = precos.length > 0 ? Math.max(...precos) : null;
  const precoMinimo = precos.length > 0 ? Math.min(...precos) : null;

  const chartData = {
    labels: historico.map((item) => formatarData(item.data)),
    datasets: [
      {
        label: "Preço da @ (R$)",
        data: precos,
        borderColor: "#f97316",
        backgroundColor: "rgba(249, 115, 22, 0.15)",
        tension: 0.3,
        fill: true,
        pointRadius: 3,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${formatarMoeda(ctx.raw)}`,
        },
      },
    },
    scales: {
      y: { ticks: { callback: (valor) => `R$ ${valor}` } },
    },
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <ImParagraphRight size={38} />
            Histórico de Cotações
          </h2>
          <p className="text-gray-600 mt-2">
            Acompanhe a evolução do preço da arroba do boi gordo (Indicador Esalq/B3)
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-wrap gap-2">
            {OPCOES_PERIODO.map((opcao) => (
              <button
                key={opcao.valor}
                onClick={() => setDias(opcao.valor)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                  dias === opcao.valor
                    ? "bg-orange-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {opcao.label}
              </button>
            ))}
          </div>
        </div>

        {erro && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mb-6">
            {erro}
          </div>
        )}

        {carregando ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando histórico...</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-orange-500">
                <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-2">
                  <GiBull size={20} /> Preço Atual
                </p>
                <p className="text-2xl font-bold text-gray-800 mt-1">
                  {formatarMoeda(precoAtual)}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-blue-500">
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Média do período
                </p>
                <p className="text-2xl font-bold text-gray-800 mt-1">
                  {formatarMoeda(precoMedio)}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-green-500">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Máxima</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">
                  {formatarMoeda(precoMaximo)}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-red-500">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Mínima</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">
                  {formatarMoeda(precoMinimo)}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <h3 className="font-semibold text-gray-800 mb-4">Evolução do preço</h3>
              <div className="h-72">
                {historico.length > 0 ? (
                  <Line data={chartData} options={chartOptions} />
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400">
                    Nenhum dado de cotação disponível para o período selecionado
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-800 mb-4">Registros</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100 border-b-2 border-gray-300">
                      <th className="px-4 py-3 text-left text-sm font-semibold">DATA</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">
                        PREÇO DA @ (R$)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...historico].reverse().map((item, idx) => (
                      <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">{formatarData(item.data)}</td>
                        <td className="px-4 py-3 text-sm font-medium">
                          {formatarMoeda(item.preco_arroba)}
                        </td>
                      </tr>
                    ))}
                    {historico.length === 0 && (
                      <tr>
                        <td colSpan="2" className="text-center py-8 text-gray-500">
                          Nenhum registro encontrado
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CotHist;
